import { CircularProgress, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import LeaveModal from 'components/common/modals/LeaveModal'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import OggHeading40 from 'components/common/text/OggHeading40'
import { ROUTES } from 'consts'
import {
  AdministrativeInfoId,
  DEFAULT_OBSERVATION_TRACKING_PRACTICE_GUIDELINES,
  DEFAULT_RETENTION_TRACKING_PRACTICE_GUIDELINES,
  GuidelineCategories
} from 'consts/hiToolsConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { isTieredHIPlanForPlan } from 'hooks/useIsTieredHIPlan'
import usePlanLimits from 'hooks/usePlanLimits'
import { isEqual } from 'lodash'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { trackEvent } from 'utils/analyticsUtils'
import AdministrativeInfoCategory from './AdministrativeInfo/AdministrativeInfoCategory'
import GuidelineCategory from './GuidelineCategory'
import usePracticeGuidelinesValidation from './usePracticeGuidelinesValidation'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    paddingBottom: 50
  },
  title: {
    marginBottom: 20
  },
  titleText: {
    lineHeight: '35px'
  },
  divider: {
    height: 1,
    backgroundColor: 'var(--text-color-6)',
    marginBottom: 20,
    width: 590
  },
  assistantTypesContainer: {
    width: 1000,
    marginBottom: 20
  },
  lastUpdatedAtLabel: {
    color: 'var(--text-color-11)'
  },
  quickLink: {
    textDecoration: 'underline',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-color-25)'
  },
  saveButton: {
    position: 'sticky',
    bottom: 0,
    marginTop: 10,
    paddingBottom: 20,
    backgroundColor: 'white'
  }
}))

const PracticeGuidelines = props => {
  const classes = useStyles()
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    hiTools: hiToolsFF = false,
    copilotControl: copilotControlFF,
    hiTieredObsRetentionCopilot: hiTieredObsRetentionCopilotFF = false
  } = useFeatureFlags()
  const { isHI: isOnHI, grinPlanKey, grinPlanGroup, withScanTracking } = usePlanLimits()

  const {
    data: practiceGuidelines,
    isLoading,
    isSaving
  } = useSelector(state => state.practiceReducer.hiPracticeGuidelines)

  const accountOwnerId = useSelector(state => state.practiceReducer.accountOwner.id)
  const practiceMembers = useSelector(state => state.profileReducer.practiceMembers.data)

  const [categoriesData, setCategoriesData] = useState()
  const [administrativeInfo, setAdministrativeInfo] = useState({})
  const [categoriesCollapse, setCategoriesCollapse] = useState({ [AdministrativeInfoId]: true })

  const { errors, validate } = usePracticeGuidelinesValidation({ administrativeInfo })

  const isTieredHIPlan = useMemo(
    () => isTieredHIPlanForPlan({ grinPlanKey, grinPlanGroup, withScanTracking }),
    [grinPlanGroup, grinPlanKey, withScanTracking]
  )

  const showRetentionObsSettings = useMemo(
    () => isTieredHIPlan && hiTieredObsRetentionCopilotFF,
    [hiTieredObsRetentionCopilotFF, isTieredHIPlan]
  )
  const visibleGuidelineCategories = useMemo(
    () => [
      GuidelineCategories.AlignersTxTracking,
      GuidelineCategories.BracesTxTracking,
      GuidelineCategories.OralHygiene,
      GuidelineCategories.TxGoals,
      GuidelineCategories.Elastics,
      GuidelineCategories.IPR,
      GuidelineCategories.RPE,
      GuidelineCategories.OtherAppliancesAndGuidelines,
      ...(showRetentionObsSettings
        ? [GuidelineCategories.RetentionTracking, GuidelineCategories.ObservationTracking]
        : [])
    ],
    [showRetentionObsSettings]
  )

  const isPermitted = useMemo(() => isOnHI && hiToolsFF, [hiToolsFF, isOnHI])

  const parsedCurrentCategoriesData = useMemo(
    () => JSON.parse(practiceGuidelines?.categories || '{}'),
    [practiceGuidelines]
  )

  const parsedCurrentAdministrativeInfo = useMemo(
    () => JSON.parse(practiceGuidelines?.administrativeInfo || '{}'),
    [practiceGuidelines]
  )

  const isDataUnsaved = useMemo(
    () =>
      !isEqual(parsedCurrentCategoriesData, categoriesData) ||
      !isEqual(parsedCurrentAdministrativeInfo, administrativeInfo),
    [categoriesData, parsedCurrentCategoriesData, administrativeInfo, parsedCurrentAdministrativeInfo]
  )

  const lastUpdatedBy = useMemo(
    () => practiceMembers?.find(pm => pm.id === practiceGuidelines?.creatorDoctorId)?.name,
    [practiceGuidelines, practiceMembers]
  )

  const lastUpdatedAt = useMemo(
    () => (practiceGuidelines?.createdAt ? moment(practiceGuidelines?.createdAt).format('MMM D, YYYY') : null),
    [practiceGuidelines]
  )

  const quickNavigations = useMemo(
    () =>
      visibleGuidelineCategories.map(categoryKey => ({
        label: t(`pages.accountSettings.practiceGuidelines.categories.${categoryKey}.quickNav`),
        key: categoryKey
      })),
    [t, visibleGuidelineCategories]
  )

  const categories = useMemo(() => {
    if (!categoriesData) {
      return null
    }

    return visibleGuidelineCategories.reduce(
      (categories, categoryKey) => ({
        ...categories,
        [categoryKey]: {
          ...(categoriesData?.[categoryKey] || {})
        }
      }),
      {}
    )
  }, [categoriesData, visibleGuidelineCategories])

  const handleCategoryCollapseClicked = useCallback(
    ({ categoryKey, shouldCollapse }) => {
      const isCollapsed = shouldCollapse ?? !(categoriesCollapse[categoryKey] ?? true)
      trackEvent('Practice guidelines - Category collapse toggled', {
        category: categoryKey,
        isCollapsed
      })

      setCategoriesCollapse({
        ...categoriesCollapse,
        [categoryKey]: isCollapsed
      })
    },
    [categoriesCollapse]
  )

  const handleQuickNavClick = useCallback(
    categoryKey => {
      trackEvent('Practice guidelines - Quick navigation clicked', {
        category: categoryKey
      })
      handleCategoryCollapseClicked({ categoryKey, shouldCollapse: false })
    },
    [handleCategoryCollapseClicked]
  )

  const handleCategoryChanged = useCallback(
    updatedCategory => {
      setCategoriesData({
        ...categoriesData,
        ...updatedCategory
      })
    },
    [categoriesData]
  )

  const handleSave = useCallback(() => {
    const isValid = validate()
    trackEvent('Practice guidelines - Save changes clicked', {
      categoriesData,
      isValid
    })

    if (!isValid) {
      return
    }

    dispatch(
      Actions.savePracticeGuidelines({
        categories: categoriesData,
        administrativeInfo,
        copilotControlFF
      })
    )
  }, [validate, categoriesData, dispatch, administrativeInfo, copilotControlFF])

  useEffect(() => {
    if (accountOwnerId) {
      dispatch(Actions.fetchPracticeGuidelines())
    }
  }, [accountOwnerId, dispatch])

  useEffect(() => {
    if (parsedCurrentCategoriesData) {
      const nextCategoriesData = showRetentionObsSettings
        ? {
            ...parsedCurrentCategoriesData,
            [GuidelineCategories.RetentionTracking]:
              parsedCurrentCategoriesData?.[GuidelineCategories.RetentionTracking] ||
              DEFAULT_RETENTION_TRACKING_PRACTICE_GUIDELINES,
            [GuidelineCategories.ObservationTracking]:
              parsedCurrentCategoriesData?.[GuidelineCategories.ObservationTracking] ||
              DEFAULT_OBSERVATION_TRACKING_PRACTICE_GUIDELINES
          }
        : parsedCurrentCategoriesData

      setCategoriesData(nextCategoriesData)
    }
  }, [parsedCurrentCategoriesData, showRetentionObsSettings])

  useEffect(() => {
    setCategoriesCollapse(prev =>
      visibleGuidelineCategories.reduce(
        (categories, categoryKey) => ({
          ...categories,
          [categoryKey]: prev[categoryKey] ?? true
        }),
        { [AdministrativeInfoId]: prev[AdministrativeInfoId] ?? true }
      )
    )
  }, [visibleGuidelineCategories])

  useEffect(() => {
    if (parsedCurrentAdministrativeInfo) {
      setAdministrativeInfo(parsedCurrentAdministrativeInfo)
    }
  }, [parsedCurrentAdministrativeInfo])

  useEffect(() => {
    if (!isPermitted) {
      history.push(ROUTES.ACCOUNT_SETTINGS)
    }
  }, [history, isPermitted])

  return isLoading || !categories ? (
    <CircularProgress size={35} />
  ) : isPermitted ? (
    <>
      <LeaveModal blockNavigation={!isLoading && isDataUnsaved} />
      <Grid container direction="column" className={classes.root}>
        <Grid item className={classes.title}>
          <Grid container alignItems="flex-end" spacing={2}>
            <Grid item>
              <OggHeading40 className={classes.titleText}>
                {copilotControlFF
                  ? t('pages.accountSettings.practiceGuidelines.copilotTitle')
                  : t('pages.accountSettings.practiceGuidelines.title')}
              </OggHeading40>
            </Grid>
            {lastUpdatedAt && (
              <Grid item>
                <DazzedParagraph12 className={classes.lastUpdatedAtLabel}>
                  {t('pages.accountSettings.practiceGuidelines.lastUpdated', {
                    updatedAt: lastUpdatedAt,
                    lastUpdatedBy
                  })}
                </DazzedParagraph12>
              </Grid>
            )}
          </Grid>
        </Grid>
        <div className={classes.divider} />
        <Grid item className={classes.assistantTypesContainer}>
          <Grid container spacing={1}>
            <Grid item>
              <DazzedParagraph14>{t('pages.accountSettings.practiceGuidelines.assistantTypes')}</DazzedParagraph14>
            </Grid>
            {quickNavigations.map(nav => (
              <Grid item key={`${nav.key}-quick-link`}>
                <a href={`#${nav.key}`} onClick={() => handleQuickNavClick(nav.key)} className={classes.quickLink}>
                  {nav.label}
                </a>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <AdministrativeInfoCategory
                id={AdministrativeInfoId}
                isCollapsed={categoriesCollapse[AdministrativeInfoId]}
                onCollapseClick={handleCategoryCollapseClicked}
                administrativeInfo={administrativeInfo}
                setAdministrativeInfo={setAdministrativeInfo}
                errors={errors?.administrativeInfo}
              />
            </Grid>
            {Object.keys(categories).map(categoryKey => (
              <Grid item key={categoryKey}>
                <GuidelineCategory
                  type={categoryKey}
                  onChange={handleCategoryChanged}
                  isEnabled={categories?.[categoryKey]?.isEnabled}
                  note={categories?.[categoryKey]?.note}
                  items={categories?.[categoryKey]?.items}
                  isCollapsed={categoriesCollapse[categoryKey] ?? true}
                  onCollapseClick={handleCategoryCollapseClicked}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item className={classes.saveButton}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <PrimaryButton
                label={t('general.save')}
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!isDataUnsaved}
                small
              />
            </Grid>
            {errors.general && (
              <Grid item>
                <DazzedParagraph14 strong color="red">
                  {errors.general}
                </DazzedParagraph14>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  ) : (
    <></>
  )
}

export default PracticeGuidelines
